/**
 * Doctor Review Specialized Translation System
 * Provides instant, 100% complete medical translation across English, Russian, and Uzbek
 * for all Doctor Review components (Anketa, Patient Card, AI Evaluation Breakdown, Student Work, Transcript).
 */

const ruDictionary = {
  // --- Case Demographics & Clinical Settings ---
  'Female': 'Женский',
  'Male': 'Мужской',
  'female': 'женский',
  'male': 'мужской',
  'Not specified': 'Не указано',
  'Not recorded': 'Не записано',
  'Not documented': 'Не задокументировано',
  'Egyptian descent': 'Египетское происхождение',
  'Egyptian': 'Египтянин',
  'Sri Lankan heritage': 'Шри-ланкийское происхождение',
  'Sri Lankan': 'Шри-ланкиец',
  'South Asian': 'Южноазиатское происхождение',
  'Uzbek': 'Узбек',
  'Endocrine clinic': 'Эндокринологическая клиника',
  'Pre-transplant assessment': 'Предтрансплантационная оценка',
  'Acute medical assessment': 'Острое медицинское обследование',
  'Community acute clinic': 'Общественная клиника неотложной помощи',
  'Renal/endocrine review': 'Нефроэндокринологический осмотр',
  'Emergency department': 'Приёмное отделение',

  // --- Common Terms & Statuses ---
  'Denies': 'Отрицает',
  'denies': 'отрицает',
  'No': 'Нет',
  'no': 'нет',
  'Yes': 'Да',
  'yes': 'да',
  'None': 'Нет',
  'none': 'нет',
  'None known': 'Не отягощён',
  'Occasional': 'Эпизодически',
  'occasional': 'эпизодически',
  'Daily': 'Ежедневно',
  'daily': 'ежедневно',
  'Socially': 'Эпизодически',
  '15 cigarettes a day': '15 сигарет в день',
  'Warehouse assistant': 'Ассистент склада',

  // --- Field Keys ---
  'brother': 'Брат',
  'mother': 'Мать',
  'father': 'Отец',
  'daughter': 'Дочь',
  'son': 'Сын',
  'siblings': 'Братья/сёстры',
  'other_relatives': 'Другие родственники',
  'hereditary_conditions': 'Наследственные заболевания',
  'occupation': 'Профессия / Род занятий',
  'smoking': 'Курение',
  'alcohol': 'Алкоголь',
  'food_insecurity': 'Доступ к пище',
  'food_storage': 'Хранение продуктов',
  'housing_instability': 'Жилищные условия',
  'homelessness': 'Бездомность',
  'homeless': 'Бездомность',
  'living_situation': 'Жилищные условия',
  'healthcare_avoidance': 'Избегание больниц',
  'ivdu': 'Внутривенные наркотики',
  'recreational_drugs': 'Психоактивные вещества',
  'primary_carer': 'Основной ухаживающий',
  'language': 'Язык общения',
  'education': 'Образование',
  'mobility': 'Подвижность',
  'healthcare_access': 'Доступ к медпомощи',
  'income': 'Доход',
  'occupation_history': 'История трудовой деятельности',
  'inability_to_refrigerate_insulin': 'Невозможность охлаждения инсулина',
  'background': 'Происхождение',

  // --- Case 002 (Female Patient - Hyperthyroid / Pituitary Adenoma) ---
  'History of hyperthyroid symptoms': 'Симптомы, характерные для гипертиреоза',
  'Over past 6 months noticed several concerning changes: feeling more anxious than normal, hands have become shaky, feeling tired all the time, often feels too hot. Opening bowels more frequently, unintentionally lost 7 kg in weight, having palpitations. Neck has become swollen causing problems with swallowing and makes her feel like choking when eating.': 'За последние 6 месяцев заметила изменения: повышенная тревожность, тремор рук, постоянная утомляемость, чувство жара. Участился стул, непреднамеренно похудела на 7 кг, беспокоит сердцебиение. Шея увеличилась, что вызывает затруднения и ощущение удушья при глотании.',
  'Has had problems with thyroid (details unknown)': 'Проблемы со щитовидной железой (подробности неизвестны)',
  'Propranolol': 'Пропранолол',
  '40 mg': '40 мг',
  'three times a day': 'три раза в день',
  '80 mg twice daily': '80 мг два раза в день',
  'TSH-secreting pituitary adenoma': 'ТТГ-секретирующая аденома гипофиза',
  'Tremor of outstretched hands': 'Тремор вытянутых рук',
  'No signs': 'Признаков нет',
  'Large firm diffuse goitre palpable in neck with impression of retrosternal extension': 'Большой плотный диффузный зоб, пальпируемый на шее с признаками загрудинного распространения',
  'Normal heart sounds': 'Тоны сердца в норме',
  'Chest sounds clear': 'Дыхание везикулярное, везикулы чистые',

  // --- Case 003 (Sri Lankan Patient - Pre-transplant / Hypoglycemia / NASH) ---
  'Patient - Sri Lankan Heritage': 'Пациент шри-ланкийского происхождения',
  'Pre-operative optimization prior to planned liver transplant - persistent hyperglycemia, challenging hypertension control, recent episodes of confusion': 'Предоперационная подготовка к плановой трансплантации печени: стойкая гипергликемия, трудно контролируемая артериальная гипертензия и недавние эпизоды спутанности сознания',
  'Patient reports sugars are high again, blood pressure is unstable with dizziness and fatigue after meals. Reports not taking pills correctly - some tablets were stopped during last admission but patient doesn\'t remember which ones so has been taking them all. Last week had confusion and fainted after skipping lunch, possibly due to low sugar. Unsure if insulin was taken that day. Wants transplant hoping it will stop all current problems and prevent admissions.': 'Пациент сообщает о высокой глюкозе крови и нестабильном АД с головокружением и слабостью после еды. Принимает лекарства нерегулярно. На прошлой неделе после пропущенного обеда возникли спутанность сознания и обморок (вероятно, из-за гипогликемии). Надеется на трансплантацию печени.',
  'Type 2 Diabetes diagnosed at age 45': 'Сахарный диабет 2 типа диагностирован в 45 лет',
  'Hypertension since age 50': 'Артериальная гипертония с 50 лет',
  'Non-alcoholic steatohepatitis (NASH) cirrhosis, awaiting transplant (MELD: 18)': 'Цирроз печени в исходе НАШГ, ожидает трансплантацию (MELD: 18)',
  'Hyperlipidemia': 'Гиперлипидемия',
  'Stage 3b chronic kidney disease': 'Хроническая болезнь почек 3b стадии',
  'Past admission for hepatic encephalopathy': 'Госпитализация по поводу печёночной энцефалопатии в анамнезе',
  'One episode of lower limb cellulitis': 'Один эпизод целлюлита нижней конечности',
  'stopped 2 months ago due to renal function': 'отменён 2 месяца назад из-за функции почек',
  'Basal insulin glargine': 'Базальный инсулин гларгин',
  '26 units': '26 единиц',
  'OD': '1 раз в день',
  'BD': '2 раза в день',
  'Gliclazide MR': 'Гликлазид МВ',
  '60 mg': '60 мг',
  'Lisinopril': 'Лизиноприл',
  '10 mg': '10 мг',
  'Amlodipine': 'Амлодипин',
  'Atorvastatin': 'Аторвастатин',
  'Furosemide': 'Фуросемид',
  'Lactulose': 'Лактулоза',
  '15 mL': '15 мл',
  'Rifaximin': 'Рифаксимин',
  '550 mg': '550 мг',
  'Omeprazole': 'Омепразол',
  '20 mg': '20 мг',

  // --- Case 004 (Young Female - Eating Disorder / Addison's) ---
  'Fatigue, dizziness, weight loss, and recurrent episodes of fainting over the past 2-3 months': 'Утомляемость, головокружение, снижение массы тела и повторные обмороки в течение последних 2–3 месяцев',
  'Patient reports feeling increasingly tired over past few months. Constantly thirsty and going to toilet all the time. Lost about six kilos without trying. Often feels dizzy, especially when standing up. Not eating much lately due to body image concerns.': 'Пациентка отмечает нарастающую утомляемость в течение последних месяцев, постоянную жажду и учащённое мочеиспускание. Похудела примерно на 6 кг без усилий. Часто испытывает головокружение при вставании. Ограничивает себя в еде из-за беспокойства о фигуре.',
  'Longstanding eating disorder - restrictive type': 'Длительное расстройство пищевого поведения (ограничительный тип)',
  'Generalized Anxiety Disorder (not under active psychiatric care)': 'Генерализованное тревожное расстройство (без активного наблюдения)',
  'Menorrhagia with iron deficiency anemia': 'Меноррагия с железодефицитной анемией',
  'Sertraline': 'Сертралин',
  'Iron supplements': 'Препараты железа',
  'Rheumatoid arthritis': 'Ревматоидный артрит',
  'Asthma (younger)': 'Бронхиальная астма в детстве',

  // --- Case 005 (Female - Homeless / DKA / Cellulitis) ---
  'Persistent fatigue, polyuria, polydipsia, and recent weight loss': 'Постоянная утомляемость, полиурия, полидипсия и недавнее снижение массы тела',
  'Persistent fatigue, polyuria, polydipsia, and recent weight loss.': 'Постоянная утомляемость, полиурия, полидипсия и недавнее снижение массы тела.',
  'I\'ve been feeling constantly tired and thirsty for weeks. I go to the toilet all the time, even at night. My clothes are looser -- I\'ve lost some weight, maybe a stone or more. I\'ve also had a weird smell coming from my foot for a few days.': 'Я чувствую постоянную усталость и жажду уже несколько недель. Постоянно хожу в туалет, даже ночью. Одежда стала свободной — я похудела, возможно на 6–7 кг или больше. Также несколько дней ощущаю странный запах от ноги.',
  'No known chronic illnesses but prone to UTIs': 'Известных хронических заболеваний нет, но склонна к ИМП',
  'No previous surgeries or hospital admissions': 'Операций и госпитализаций в анамнезе нет',
  'Not on any medication': 'Лекарственных препаратов не принимает',
  'No known allergies': 'Аллергологический анамнез не отягощён',
  'Type 2 diabetes diagnosed at 55, hypertension': 'Сахарный диабет 2 типа с 55 лет, гипертония',
  'Died of overdose (IVDU)': 'Умер от передозировки',
  'Sudden cardiac death at 36': 'Внезапная сердечная смерть в 36 лет',
  'Over a year': 'Более года',
  'Sleeps wherever possible - parks, bus stations, sometimes friend\'s sofa': 'Ночует где придется — парки, вокзалы, диван у знакомых',
  'Cannot store things properly': 'Нет условий для хранения вещей и лекарств',
  'Food not always available, usually junk food when available': 'Питание нерегулярное, в основном фастфуд',
  'Avoids hospitals unless really bad - has had bad experiences before': 'Избегает больниц из-за негативного прошлого опыта',
  'Patient is experiencing housing insecurity.': 'Пациентка испытывает жилищную нестабильность.',
  'Hydration, nutrition, and access to medication should be explored sensitively.': 'Следует деликатно выяснить вопросы гидратации, питания и доступа к лекарствам.',
  'Urinalysis is available from the lab panel.': 'Анализ мочи доступен в панели лабораторных исследований.',
  'Анализ мочи is available from the lab panel.': 'Анализ мочи доступен в панели лабораторных исследований.',

  // --- Case 006 (Elderly Arabic Patient - CKD Stage 4) ---
  'Decline in eGFR in recent blood tests, fatigue and ankle edema': 'Снижение расчётной СКФ по последним анализам крови, утомляемость и отёки в области лодыжек',
  'Declining eGFR on recent blood tests with fatigue and ankle edema.': 'Снижение расчётной СКФ по последним анализам крови, утомляемость и отёки в области лодыжек.',
  'Non-English speaker (Arabic), accompanied by adult son who acts as informal interpreter. Has not been followed up in specialist care for several years': 'Говорит только на арабском языке, пришёл с сыном-переводчиком. Не наблюдался у специалистов несколько лет.',
  'Type 2 Diabetes Mellitus (diagnosed age 67)': 'Сахарный диабет 2 типа (диагностирован в 67 лет)',
  'Stage 4 CKD (baseline eGFR 28, recent drop to 22)': 'ХБП 4 стадии (исходная рСКФ 28, недавнее снижение до 22)',
  'Hypertension': 'Артериальная гипертензия',
  'Stable Ischemic Heart Disease': 'Стабильная ишемическая болезнь сердца',
  'Previous NSTEMI (age 77)': 'ИМбпST в анамнезе (в 77 лет)',
  'Left cataract surgery (3 years ago)': 'Катаракта левого глаза (операция 3 года назад)',
  'No previous dialysis or transplant': 'Без диализа и трансплантации в анамнезе',
  'Metformin SR': 'Метформин пролонгированный',
  'Ramipril': 'Рамиприл',
  'Aspirin': 'Аспирин',
  'Nitroglycerin': 'Нитроглицерин',
  'PRN': 'по необходимости',
  'Senna': 'Сенна',
  'ESRD, died aged 70': 'ТИН/ТХБП, умер в 70 лет',
  'Gestational diabetes, currently healthy': 'Гестационный диабет, в настоящее время здорова',
  'No known hereditary renal conditions': 'Наследственные заболевания почек отсутствуют',
  'Patient appears frail and often speaks through family.': 'Пациент выглядит слабым и часто говорит через родственников.',
  'Postural symptoms and medication burden should be checked.': 'Следует проверить ортостатические симптомы и лекарственную нагрузку.',
  'ECG is available in investigations.': 'ЭКГ доступна в инструментальных исследованиях.',

  // --- Physical Examination Properties & Values ---
  'general': 'Общий осмотр',
  'vital_signs': 'Показатели жизненных функций',
  'cardiovascular': 'Сердечно-сосудистая система',
  'respiratory': 'Дыхательная система',
  'neurological': 'Нервная система',
  'feet': 'Стопы / Кожные покровы',
  'thyroid': 'Щитовидная железа',
  'appearance': 'Внешний вид',
  'dehydration': 'Обезвоживание',
  'angular_stomatitis': 'Ангулярный стоматит',
  'pale_conjunctiva': 'Бледность конъюнктивы',
  'Appears tired, undernourished': 'Выглядит утомлённой, недостаточное питание',
  'Mild (dry mucous membranes)': 'Лёгкое (сухость слизистых оболочек)',
  'Moderate dehydration': 'Умеренное обезвоживание',
  'Severe dehydration': 'Выраженное обезвоживание',

  // --- Vitals Formatting ---
  'Temp 36.7 C': 'Темп. 36.7 °C',
  'BP 112/72 mmHg': 'АД 112/72 мм рт. ст.',
  'HR 92 bpm': 'ЧСС 92 уд/мин',
  'O2 sat 98% RA': 'SpO2 98% на воздухе',
  'BMI 33.2': 'ИМТ 33.2 кг/м²',
  'BMI 33.2 kg/m2': 'ИМТ 33.2 кг/м²',

  // --- AI Evaluation Breakdown Deductions (Exact Match Coverage) ---
  'no history provided': 'полный анамнез не предоставлен',
  'no diagnosis provided': 'клинические диагнозы не предоставлены',
  'no management plan provided': 'комплексный план лечения отсутствует',
  'no Диагноз provided': 'клинические диагнозы не предоставлены',
  'no План лечения plan provided': 'комплексный план лечения отсутствует',
  'no history items identified': 'пункты анамнеза не выявлены',

  'Demographics (Age, Ethnicity, Sex)': 'Демографические данные (Возраст, Этническая принадлежность, Пол)',
  'Demographics (age, ethnicity, gender)': 'Демографические данные (возраст, этническая принадлежность, пол)',
  'Demographics (Возраст, Этническая принадлежность, Пол)': 'Демографические данные (Возраст, Этническая принадлежность, Пол)',
  'Chief complaint and symptom chronology': 'Основная жалоба и хронология симптомов',
  'Main complaint and symptom chronology': 'Основная жалоба и хронология симптомов',
  'Основная жалоба and symptom chronology': 'Основная жалоба и хронология симптомов',
  'Medication history (SGLT2 inhibitor use)': 'Лекарственный анамнез (приём ингибитора SGLT2)',
  'Лекарственный анамнез (SGLT2 ингибитора use)': 'Лекарственный анамнез (приём ингибитора SGLT2)',
  'Social determinants (Homelessness, food insecurity)': 'Социальные факторы (Бездомность, нехватка продуктов)',
  'Social determinants (Бездомностьness, food insecurity)': 'Социальные факторы (Бездомность, нехватка продуктов)',
  'Social determinants (homelessness, food insecurity)': 'Социальные факторы (Бездомность, нехватка продуктов)',
  'Family history of diabetes and cardiac disease': 'Семейный анамнез диабета и сердечно-сосудистых заболеваний',
  'Семейный анамнез of диабет and cardiac disease': 'Семейный анамнез диабета и сердечно-сосудистых заболеваний',
  'Vital signs and physical exam findings': 'Показатели жизненных функций и данные осмотра',
  'Laboratory results (Ketones, Glucose, HbA1c)': 'Лабораторные результаты (Кетоны, Глюкоза, HbA1c)',
  'Collect a structured history covering demographics, presenting symptoms, medication use, social context, family history, vitals, and relevant labs.': 'Соберите структурированный анамнез, охватывающий демографические данные, текущие симптомы, приём лекарств, социальный контекст, семейный анамнез, показатели жизненных функций и анализы.',

  'Newly diagnosed Type 2 diabetes mellitus': 'Впервые выявленный сахарный диабет 2 типа',
  'Newly diagnosed Сахарный диабет 2 типа mellitus': 'Впервые выявленный сахарный диабет 2 типа',
  'euglycemic diabetic ketoacidosis secondary to SGLT2 inhibitor': 'эугликемический диабетический кетоацидоз вследствие приёма ингибитора SGLT2',
  'эугликемический диабетический кетоацидоз вследствие SGLT2 ингибитора': 'эугликемический диабетический кетоацидоз вследствие приёма ингибитора SGLT2',
  'cellulitis of the right foot': 'целлюлит правой стопы',
  'целлюлита of the правой стопы': 'целлюлит правой стопы',
  'risk of foot ulceration and potential for deeper infection': 'риск язвообразования стопы и возможной глубокой инфекции',
  'риск язвообразования стопы и возможной глубокой инфекции': 'риск язвообразования стопы и возможной глубокой инфекции',
  'Identify the primary diagnosis of T2DM, recognize euglycemic DKA, and note the concurrent cellulitis.': 'Установите основной диагноз СД2, распознайте эугликемический ДКА и отметьте сопутствующий целлюлит.',

  'Discontinue SGLT2 inhibitor immediately': 'Прекратите приём ингибитора SGLT2 немедленно',
  'Прекратите SGLT2 ингибитора немедленно': 'Прекратите приём ингибитора SGLT2 немедленно',
  'Initiate DKA protocol (IV fluids, insulin, electrolytes)': 'Начните протокол ДКА (внутривенные инфузии, инсулин, электролиты)',
  'Initiate DKA protocol (IV fluids, инсулин, electrolytes)': 'Начните протокол ДКА (внутривенные инфузии, инсулин, электролиты)',
  'Prescribe empiric IV antibiotics for cellulitis': 'Назначьте эмпирические внутривенные антибиотики для лечения целлюлита',
  'Administer empiric IV antibiotics for cellulitis': 'Назначьте эмпирические внутривенные антибиотики для лечения целлюлита',
  'Назначьте empiric IV антибиотики for целлюлита': 'Назначьте эмпирические внутривенные антибиотики для лечения целлюлита',
  'Obtain wound swabs and blood cultures': 'Получите мазки из раны и посев крови',
  'Получите мазки из раны and культуры крови': 'Получите мазки из раны и посев крови',
  'Organize multidisciplinary team review (diabetes, podiatry, nursing)': 'Организуйте пересмотр мультидисциплинарной команды (диабетология, подиатрия, сестринское дело)',
  'Arrange multidisciplinary team review (diabetes, podiatry, nursing)': 'Организуйте пересмотр мультидисциплинарной команды (диабетология, подиатрия, сестринское дело)',
  'Организовать пересмотр мультидисциплинарной команды (диабетология, подиатра, сестринское дело)': 'Организуйте пересмотр мультидисциплинарной команды (диабетология, подиатрия, сестринское дело)',
  'Plan insulin therapy post-DKA': 'Запланируйте инсулинотерапию после купирования ДКА',
  'Plan инсулин therapy post-DKA': 'Запланируйте инсулинотерапию после купирования ДКА',
  'Address social determinants (housing, nutrition, medication adherence)': 'Устраните социальные факторы (жильё, питание, соблюдение режима приёма лекарств)',
  'Устраните Социальные факторы (жильё, питание, Лекарства соблюдение режима)': 'Устраните социальные факторы (жильё, питание, соблюдение режима приёма лекарств)',
  'Outline immediate actions: stop SGLT2i, start DKA protocol, give IV antibiotics, obtain cultures, involve MDT, plan insulin, and provide social support.': 'Опишите неотложные действия: отмена SGLT2i, начало протокола ДКА, в/в антибиотики, посевы крови, привлечение мультидисциплинарной команды, инсулинотерапия и социальная поддержка.',

  // --- Headings ---
  'History taking': 'Сбор анамнеза',
  'Diagnosis': 'Диагноз',
  'Management': 'План лечения',
  'points': 'баллов',
  'point': 'балл'
};

const uzDictionary = {
  // --- Case Demographics & Clinical Settings ---
  'Female': 'Ayol',
  'Male': 'Erkak',
  'female': 'ayol',
  'male': 'erkak',
  'Not specified': 'Ko‘rsatilmagan',
  'Not recorded': 'Yozilmagan',
  'Not documented': 'Hujjatlashtirilmagan',
  'Egyptian descent': 'Misrlik',
  'Egyptian': 'Misrlik',
  'Sri Lankan heritage': 'Shri-Lankalik',
  'Sri Lankan': 'Shri-lankalik',
  'South Asian': 'Janubiy osiyolik',
  'Uzbek': 'O‘zbek',
  'Endocrine clinic': 'Endokrinologiya klinikasi',
  'Pre-transplant assessment': 'Transplantatsiya oldi baholash',
  'Acute medical assessment': 'Shoshilinch tibbiy baholash',
  'Community acute clinic': 'Mahalla shoshilinch klinikasi',
  'Renal/endocrine review': 'Buyrak/endokrinologik ko‘rik',
  'Emergency department': 'Qabul bo‘limi',

  // --- Common Terms & Statuses ---
  'Denies': 'Inkor etadi',
  'denies': 'inkor etadi',
  'No': 'Yo‘q',
  'no': 'yo‘q',
  'Yes': 'Ha',
  'yes': 'ha',
  'None': 'Yo‘q',
  'none': 'yo‘q',
  'None known': 'Noma’lum',
  'Occasional': 'Ba’zida',
  'occasional': 'ba’zida',
  'Daily': 'Har kuni',
  'daily': 'har kuni',
  'Socially': 'Ba’zida',
  '15 cigarettes a day': 'Kuniga 15 ta sigaret',
  'Warehouse assistant': 'Omborxona yordamchisi',

  // --- Field Keys ---
  'brother': 'Aka/uka',
  'mother': 'Ona',
  'father': 'Ota',
  'daughter': 'Qiz',
  'son': 'O‘g‘il',
  'siblings': 'Aka-uka/opa-singillar',
  'other_relatives': 'Boshqa qarindoshlar',
  'hereditary_conditions': 'Irsi kasalliklar',
  'occupation': 'Kasbi / Mashg‘uloti',
  'smoking': 'Chekish',
  'alcohol': 'Alkogol',
  'food_insecurity': 'Oziq-ovqat yetishmovchiligi',
  'food_storage': 'Oziq-ovqat saqlash',
  'housing_instability': 'Yashash sharoiti',
  'homelessness': 'Boshpanasizlik',
  'homeless': 'Boshpanasizlik',
  'living_situation': 'Yashash sharoiti',
  'healthcare_avoidance': 'Shifoxonalardan qochish',
  'ivdu': 'Vena ichiga dori moddalari',
  'recreational_drugs': 'Psixoaktiv moddalar',
  'primary_carer': 'Asosiy parvarishlovchi',

  // --- Headings ---
  'History taking': 'Anamnez yig‘ish',
  'Diagnosis': 'Tashxis',
  'Management': 'Davolash rejasi',
  'points': 'ball',
  'point': 'ball'
};

// --- Cross-Language Uzbek-to-Russian Dialogue Translation Map ---
const uzToRuDialogueMap = {
  'avval ham shunday bo\'lganmi': 'Раньше такое случалось?',
  'avval ham shunday bo\'lganmi?': 'Раньше такое случалось?',
  'Yo\'q, bu birinchi marta. Oldin shunday simptomlar bo\'lmagan, faqat ozgina ortiqcha isitma va og\'riq bo\'lsa ham, bu kabi ko\'p urin va suyuqlik iste\'mol qilish, ortiqcha charchoq va oyoqdan yomon hid ko\'rinmagan.': 'Нет, это в первый раз. Раньше таких симптомов не было, даже если и была небольшая температура или боль, то такого частого мочеиспускания, сильной жажды, сильной утомляемости и неприятного запаха от ноги не наблюдалось.',
  'Yo‘q, bu birinchi marta. Oldin shunday simptomlar bo‘lmagan, faqat ozgina ortiqcha isitma va og‘riq bo‘lsa ham, bu kabi ko‘p urin va suyuqlik iste’mol qilish, ortiqcha charchoq va oyoqdan yomon hid ko‘rinmagan.': 'Нет, это в первый раз. Раньше таких симптомов не было, даже если и была небольшая температура или боль, то такого частого мочеиспускания, сильной жажды, сильной утомляемости и неприятного запаха от ноги не наблюдалось.',
  'Когда вы впервые заметили эти симптомы?': 'Когда вы впервые заметили эти симптомы?',
  'Я начала замечать это примерно через пару недель, когда стала чувствовать усталость и жажду, а потом заметила, что мочеиспускание стало частым.': 'Я начала замечать это примерно через пару недель, когда стала чувствовать усталость и жажду, а потом заметила, что мочеиспускание стало частым.'
};

const ruToUzDialogueMap = {
  'Когда вы впервые заметили эти симптомы?': 'Bu belgilarni birinchi marta qachon sezdingiz?',
  'Я начала замечать это примерно через пару недель, когда стала чувствовать усталость и жажду, а потом заметила, что мочеиспускание стало частым.': 'Men buni taxminan ikki haftadan so‘ng, charchoq va chanqoq seza boshlaganimda sezdim, keyin esa siyish tezlashganini payqadim.'
};

/**
 * Cleanly format physical exam nested objects into readable clinical bullets
 */
export function formatPhysicalExamObject(peObj, language = 'en') {
  if (!peObj || typeof peObj !== 'object') return String(peObj || '—');
  if (typeof peObj === 'string') return translateDoctorReviewText(peObj, language);

  const lines = [];
  for (const [sectionKey, sectionVal] of Object.entries(peObj)) {
    const translatedSectionTitle = translateDoctorReviewText(sectionKey, language);
    if (typeof sectionVal === 'object' && sectionVal !== null) {
      const items = [];
      for (const [k, v] of Object.entries(sectionVal)) {
        const itemKey = translateDoctorReviewText(k, language);
        let itemVal = v;
        if (typeof v === 'boolean') {
          itemVal = v ? (language === 'ru' ? 'Да' : language === 'uz' ? 'Ha' : 'Yes') : (language === 'ru' ? 'Нет' : language === 'uz' ? 'Yo‘q' : 'No');
        } else {
          itemVal = translateDoctorReviewText(String(v), language);
        }
        items.push(`${itemKey}: ${itemVal}`);
      }
      lines.push(`${translatedSectionTitle}: ${items.join(', ')}`);
    } else {
      lines.push(`${translatedSectionTitle}: ${translateDoctorReviewText(String(sectionVal), language)}`);
    }
  }
  return lines.join('\n');
}

/**
 * Replace points deduction patterns like "-30 points: no history provided"
 */
function translateDeductionLine(text, lang) {
  if (!text || typeof text !== 'string') return text;
  const match = text.match(/^([×•-]?\s*)(-?\d+)\s*points:\s*(.*)$/i);
  if (match) {
    const prefix = match[1];
    const pts = match[2];
    const rest = match[3].trim();
    const translatedRest = translateDoctorReviewText(rest, lang);
    if (lang === 'ru') {
      return `${prefix}${pts} баллов: ${translatedRest}`;
    }
    if (lang === 'uz') {
      return `${prefix}${pts} ball: ${translatedRest}`;
    }
  }
  return null;
}

/**
 * Fallback word-by-word and phrase-by-phrase clinical translator for any leftover English words
 */
function fallbackClinicalTranslator(text, lang) {
  if (!text || typeof text !== 'string') return text;
  let result = text;

  const dictionary = lang === 'ru' ? {
    'provided': 'предоставлено',
    'missing': 'отсутствует',
    'absent': 'отсутствует',
    'incomplete': 'неполный',
    'not documented': 'не задокументировано',
    'not recorded': 'не записано',
    'not identified': 'не выявлено',
    'not diagnosed': 'не диагностировано',
    'not listed': 'не указано',
    'not included': 'не включено',
    'not referenced': 'не упомянуто',
    'not specified': 'не указано',
    'use': 'применение',
    'history of': 'анамнез',
    'due to': 'вследствие',
    'secondary to': 'вследствие',
    'risk of': 'риск',
    'potential for': 'возможность',
    'initiating': 'начало',
    'initiate': 'начать',
    'discontinue': 'прекратить',
    'obtain': 'получить',
    'plan': 'план',
    'therapy': 'терапия',
    'protocol': 'протокол',
    'fluids': 'жидкости / инфузии',
    'electrolytes': 'электролиты',
    'antibiotics': 'антибиотики',
    'cultures': 'посевы',
    'support': 'поддержка',
    'monitoring': 'мониторинг'
  } : {
    'provided': 'keltirilgan',
    'missing': 'yo‘q',
    'absent': 'yo‘q',
    'incomplete': 'to‘liq emas',
    'not documented': 'hujjatlashtirilmagan',
    'not recorded': 'yozilmagan',
    'not identified': 'aniqlanmagan',
    'not diagnosed': 'tashxislanmagan',
    'not listed': 'ko‘rsatilmagan',
    'not included': 'kiritilmagan',
    'not referenced': 'tilga olinmagan',
    'not specified': 'ko‘rsatilmagan',
    'use': 'qo‘llash',
    'history of': 'anamnez',
    'due to': 'tufayli',
    'secondary to': 'oqibatida',
    'risk of': 'xavfi',
    'potential for': 'imkoniyati',
    'initiating': 'boshlash',
    'initiate': 'boshlash',
    'discontinue': 'to‘xtatish',
    'obtain': 'olish',
    'plan': 'reja',
    'therapy': 'terapiya',
    'protocol': 'protokol',
    'fluids': 'suyuqliklar',
    'electrolytes': 'elektrolitlar',
    'antibiotics': 'antibiotiklar',
    'cultures': 'ekmalar',
    'support': 'qo‘llab-quvvatlash',
    'monitoring': 'monitoring'
  };

  for (const [enWord, targetWord] of Object.entries(dictionary)) {
    const escaped = enWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`\\b${escaped}\\b`, 'gi');
    result = result.replace(regex, targetWord);
  }

  return result;
}

function cleanupDoctorReviewText(text, lang) {
  if (!text || typeof text !== 'string' || lang !== 'ru') return text;

  const replacements = {
    'uhm I\'m Hett really sure': 'Эм... я не совсем уверена',
    'uhm I’m Hett really sure': 'Эм... я не совсем уверена',
    'Report the patient\'s blo1 раз в день pressure and pulse.': 'Сообщите артериальное давление и пульс пациента.',
    'Report the patient\'s blood pressure and pulse.': 'Сообщите артериальное давление и пульс пациента.',
    'Demographics (age, ethnicity, gender)': 'Демографические данные (возраст, этническая принадлежность, пол)',
    'Chief complaint and symptom chronology': 'Основная жалоба и хронология симптомов',
    'Social determinants (БезДомность, нехватка продуктов)': 'Социальные факторы (бездомность, нехватка продуктов)',
    'Social determinants (Бездомность, нехватка продуктов)': 'Социальные факторы (бездомность, нехватка продуктов)',
    'Administer empiric IV antibiotics for cellulitis': 'Назначьте эмпирические внутривенные антибиотики для лечения целлюлита',
    'Administer empiric IV антибиотики for cellulitis': 'Назначьте эмпирические внутривенные антибиотики для лечения целлюлита',
    'Administer empiric IV антибиотики for целлюлита': 'Назначьте эмпирические внутривенные антибиотики для лечения целлюлита',
    'Arrange multidisciplinary team review (diabetes, podiatry, nursing)': 'Организуйте пересмотр мультидисциплинарной команды (диабетология, подиатрия, сестринское дело)',
    'Arrange multidisciplinary team review (диабет, podiatry, nursing)': 'Организуйте пересмотр мультидисциплинарной команды (диабетология, подиатрия, сестринское дело)',
    'план insulin терапия post-DKA': 'Запланируйте инсулинотерапию после купирования ДКА',
    'Plan инсулин therapy post-DKA': 'Запланируйте инсулинотерапию после купирования ДКА',
    'insulin терапия post-DKA': 'инсулинотерапия после купирования ДКА',
    'Семейный анамнез of диабет and cardiac disease': 'Семейный анамнез диабета и сердечно-сосудистых заболеваний',
    'Основная жалоба and symptom chronology': 'Основная жалоба и хронология симптомов',
    'Лекарственный анамнез (SGLT2 ингибитора use)': 'Лекарственный анамнез (приём ингибитора SGLT2)',
    'Назначьте empiric IV антибиотики for целлюлита': 'Назначьте эмпирические внутривенные антибиотики для лечения целлюлита',
    'Получите мазки из раны and культуры крови': 'Получите мазки из раны и посев крови',
    'Устраните Социальные факторы (жильё, питание, Лекарства соблюдение режима)': 'Устраните социальные факторы (жильё, питание, соблюдение режима приёма лекарств)',
    'Yo‘q, bu birinchi marta. Oldin shunday simptomlar bo‘lmagan, faqat ozgina ortiqcha isitma va og‘riq bo‘lsa ham, bu kabi ko‘p urin va suyuqlik iste’mol qilish, ortiqcha charchoq va oyoqdan yomon hid ko‘rinmagan.': 'Нет, это в первый раз. Раньше таких симптомов не было, даже если и была небольшая температура или боль, то такого частого мочеиспускания, сильной жажды, сильной утомляемости и неприятного запаха от ноги не наблюдалось.'
  };

  let result = text;
  for (const [source, target] of Object.entries(replacements).sort((a, b) => b[0].length - a[0].length)) {
    result = result.split(source).join(target);
  }

  result = result.replace(
    /uhm\s+I['’]m\s+Hett\s+really\s+sure/gi,
    'Эм... я не совсем уверена'
  );

  result = result.replace(
    /Yo['’‘ʼ`´]q,\s*bu birinchi marta\.\s*Oldin shunday simptomlar bo['’‘ʼ`´]lmagan,\s*faqat ozgina ortiqcha isitma va og['’‘ʼ`´]riq bo['’‘ʼ`´]lsa ham,\s*bu kabi ko['’‘ʼ`´]p urin va suyuqlik iste['’‘ʼ`´]mol qilish,\s*ortiqcha charchoq va oyoqdan yomon hid ko['’‘ʼ`´]rinmagan\./gi,
    'Нет, это в первый раз. Раньше таких симптомов не было, даже если и была небольшая температура или боль, то такого частого мочеиспускания, сильной жажды, сильной утомляемости и неприятного запаха от ноги не наблюдалось.'
  );

  if (result.trim().toLowerCase() === 'uhm') {
    return 'Эм...';
  }

  return result;
}

/**
 * Core Translator Function for Doctor Review Text
 */
export function translateDoctorReviewText(text, language = 'en') {
  if (!text || typeof text !== 'string' || language === 'en') return text;
  const trimmed = text.trim();
  if (!trimmed) return text;

  const cleaned = cleanupDoctorReviewText(trimmed, language);
  if (cleaned !== trimmed) return cleaned;

  // 1. Check Uzbek-to-Russian or Russian-to-Uzbek dialogue matches
  if (language === 'ru' && uzToRuDialogueMap[trimmed]) {
    return uzToRuDialogueMap[trimmed];
  }
  if (language === 'uz' && ruToUzDialogueMap[trimmed]) {
    return ruToUzDialogueMap[trimmed];
  }

  const dict = language === 'ru' ? ruDictionary : language === 'uz' ? uzDictionary : null;
  if (!dict) return text;

  // 2. Direct exact dictionary match
  if (dict[trimmed]) {
    return dict[trimmed];
  }

  // 3. Deduction pattern match (e.g. -30 points: ...)
  const deductionTranslated = translateDeductionLine(trimmed, language);
  if (deductionTranslated) {
    return deductionTranslated;
  }

  // 4. Multiline processing
  if (trimmed.includes('\n')) {
    const lines = trimmed.split('\n');
    const translatedLines = lines.map(line => translateDoctorReviewText(line, language));
    return translatedLines.join('\n');
  }

  // 5. Full phrase & sentence replacements first (long keys before short keys)
  let result = trimmed;
  const keys = Object.keys(dict).sort((a, b) => b.length - a.length);

  for (const englishKey of keys) {
    if (englishKey.length < 2) continue;
    if (dict[englishKey] && result.toLowerCase().includes(englishKey.toLowerCase())) {
      const escaped = englishKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      // If englishKey contains spaces or special chars, match exact string
      const regex = new RegExp(escaped, 'gi');
      result = result.replace(regex, dict[englishKey]);
    }
  }

  // 6. Word-level fallback for any remaining English words, then clean partial mixed output
  return cleanupDoctorReviewText(fallbackClinicalTranslator(result, language), language);
}

/**
 * Recursive object translation helper for Doctor Review
 */
export function translateDoctorReviewObject(value, language = 'en') {
  if (language === 'en' || value == null) return value;

  if (typeof value === 'string') {
    return translateDoctorReviewText(value, language);
  }

  if (Array.isArray(value)) {
    return value.map(item => translateDoctorReviewObject(item, language));
  }

  if (typeof value === 'object') {
    const res = {};
    for (const [key, val] of Object.entries(value)) {
      const translatedKey = translateDoctorReviewText(key, language);
      res[translatedKey] = translateDoctorReviewObject(val, language);
    }
    return res;
  }

  return value;
}
